import { FadeUp, PageShell, Eyebrow, SectionHeading } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  usePageTitle("About Us — Sai Leela Decor | Turnkey Construction, Maharashtra");
  const c = useContent();
  const CLIENTS = c.clients;
  const CONSULTANTS = c.consultants;
  const RESIDENTIAL = c.residentialClients || [];
  const PROCESS = c.process;
  return (
    <PageShell testid="about-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24">
        <FadeUp>
          <Eyebrow className="mb-5">About the Company</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone max-w-3xl">
            Building trust, <span className="italic text-bronze">styling spaces.</span>
          </h1>
        </FadeUp>
      </section>

      <section data-testid="about-story" className="border-y border-white/[0.07] bg-ink/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <div className="relative overflow-hidden">
                <img src={c.about.storyImage} alt="ShivShakti House terraces and pool illuminated at dusk" className="w-full h-auto transition-transform duration-700 ease-luxury hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent" />
                <p className="absolute bottom-5 left-5 text-[10px] uppercase tracking-[0.3em] text-bone/80">ShivShakti House · Pawana</p>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 flex flex-col justify-center">
            <FadeUp delay={0.1}>
              <Eyebrow className="mb-6">Who We Are</Eyebrow>
              <p className="font-serif text-2xl sm:text-3xl font-light leading-snug text-bone">
                {c.about.storyLead}
              </p>
              <p className="mt-6 text-base font-light leading-relaxed text-stone2">
                {c.about.storyBody}
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="about-quote" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28 text-center">
        <FadeUp>
          <blockquote className="font-serif italic text-2xl sm:text-3xl lg:text-4xl font-light leading-snug text-bone">
            {c.about.quote}
          </blockquote>
        </FadeUp>
      </section>

      <section data-testid="about-clients" className="border-y border-white/[0.07] bg-ink/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <SectionHeading eyebrow="Clientele" title="Trusted by industry and institutions" className="mb-14" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
            {CLIENTS.map((c, i) => (
              <FadeUp key={c} delay={0.03 * i} className="bg-obsidian p-6 flex items-center min-h-[96px] transition-colors duration-500 hover:bg-bronze/[0.05]">
                <p className="text-sm font-light text-bone/80 tracking-wide">{c}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-14">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6">Residential Co-operative Housing Projects</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
              {RESIDENTIAL.map((cname, i) => (
                <FadeUp key={cname} delay={0.03 * i} className="bg-obsidian p-6 flex items-center min-h-[96px] transition-colors duration-500 hover:bg-bronze/[0.05]">
                  <p className="text-sm font-light text-bone/80 tracking-wide">{cname}</p>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-4">Turnkey Bungalow Collaborations</p>
            <p className="font-mono text-xs text-muted2 leading-loose">{CONSULTANTS.join("  ·  ")}</p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-process" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <SectionHeading eyebrow="The Process" title="From site study to handover" className="mb-14" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {PROCESS.map((p, i) => (
            <FadeUp key={p.num} delay={0.06 * i} className="border-b border-white/[0.07] py-9">
              <div className="flex items-start gap-6">
                <span className="font-mono text-xs text-bronze pt-2">{p.num}</span>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-light text-bone">{p.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-stone2 max-w-md">{p.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2} className="mt-16 text-center">
          <Link to="/contact" data-testid="about-cta-btn" className="inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light">
            Start the Conversation <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </section>
    </PageShell>
  );
}
